/**
 * PaymentsSummary — shared "Payments: $X.XX (count)" line with hover
 * tooltip showing the per-payment breakdown. Single source of truth so
 * the expanded-itinerary segment card AND the Add/Edit Segment dialogs'
 * draft-payments section render the same summary with the same UX.
 *
 * Pure presentational component — caller owns the data shape and any
 * action buttons it wants to render alongside the summary (passed in as
 * `actions`).
 *
 * Props:
 *   payments              — array of payment objects from the API
 *   formatCurrency        — currency formatter (parent owns locale)
 *   getPaymentMethodName  — id → display name lookup
 *   actions               — optional ReactNode rendered to the right of
 *                           the summary (e.g. "+ Add" button)
 *   testId                — root element data-testid override
 */
import { DollarSign } from "lucide-react";

const PaymentsSummary = ({
  payments = [],
  formatCurrency,
  getPaymentMethodName,
  actions = null,
  testId = "payments-summary",
}) => {
  const totalAmount = payments.reduce((sum, p) => {
    if (p.currency === "POINTS") return sum;
    return sum + (p.base_currency_amount != null ? p.base_currency_amount : (p.amount || 0));
  }, 0);

  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      {/* `group` + `cursor-help` give us the hover-to-reveal tooltip that
          the user explicitly called out as a feature they want shared
          between the inline itinerary view and the dialog forms. */}
      <div className="relative group cursor-help">
        <div className="flex items-center text-sm font-medium text-gray-700 dark:text-slate-200">
          <DollarSign className="mr-1 h-4 w-4" />
          {payments.length > 0 ? (
            <span>
              Payments: <span className="font-bold text-green-600 dark:text-green-400">{formatCurrency(totalAmount)}</span>
              <span className="text-xs text-gray-500 dark:text-slate-400 ml-1">({payments.length})</span>
            </span>
          ) : (
            <span className="text-gray-400 dark:text-slate-500">No payments</span>
          )}
        </div>

        {payments.length > 0 && (
          <div className="absolute bottom-full left-0 mb-2 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg min-w-max max-w-md">
            <div className="font-bold mb-2 border-b border-gray-700 pb-1">Payment Breakdown</div>
            <div className="space-y-2">
              {payments.map((payment) => {
                const statusLower = (payment.status || "").toLowerCase();
                let badgeClass = "bg-gray-700 text-gray-200";
                if (statusLower === "paid" || statusLower === "completed") badgeClass = "bg-green-600 text-white";
                else if (statusLower === "planning" || statusLower === "pending" || statusLower === "planned") badgeClass = "bg-amber-500 text-white";
                else if (statusLower === "cancelled" || statusLower === "refunded") badgeClass = "bg-red-600 text-white";

                return (
                  <div key={payment.id} className="border-b border-gray-700 pb-1 last:border-0">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1">
                        <div className="font-medium">{payment.description?.replace(/_/g, " ") || "Fare"}</div>
                        <div className="text-gray-400">
                          {formatCurrency(payment.amount, payment.currency)}
                          {payment.base_currency_amount != null && payment.currency !== "USD" && (
                            <span> → {formatCurrency(payment.base_currency_amount)}</span>
                          )}
                        </div>
                        {getPaymentMethodName && getPaymentMethodName(payment.payment_method_id) && (
                          <div className="text-gray-400 mt-1">💳 {getPaymentMethodName(payment.payment_method_id)}</div>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${badgeClass}`}>
                        {payment.status}
                      </span>
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-between font-bold pt-1 border-t border-gray-600">
                <span>Total:</span>
                <span className="text-green-400">{formatCurrency(totalAmount)}</span>
              </div>
            </div>
            <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
          </div>
        )}
      </div>
      {actions}
    </div>
  );
};

export default PaymentsSummary;
